/**
 * Integrytas Group - Unified Processing Pipeline (UPP)
 * Page: UPP.DEFINITIONS
 *
 * The comprehensive builder: define an export/import profile (direction, format,
 * validation behavior) and its field mappings (source→target, order, include,
 * transform + config, validation rules). The same definitions drive the engine
 * from the simple export front-end and from import.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, Plus, RefreshCw } from 'lucide-react';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'UPP.DEFINITIONS';
registerUdcHost(PAGE_CODE, 'upp_definition', 'Export / Import Definitions');

const TRANSFORMS = ['NONE', 'DATE', 'NUMBER', 'BOOL_YN', 'UPPER', 'LOWER', 'TRIM', 'CONCAT', 'LOOKUP', 'CUSTOM'];

const DEF_FIELD_DEFS: FieldDef[] = [
  { key: 'code',       type: 'text',   label: 'Code' },
  { key: 'name',       type: 'text',   label: 'Name',             col_span: 2 },
  { key: 'direction',  type: 'select', label: 'Direction' },
  { key: 'format',     type: 'select', label: 'Format' },
  { key: 'on_invalid', type: 'select', label: 'On Invalid' },
  { key: 'entity',     type: 'text',   label: 'Entity (optional)', col_span: 2 },
];
const DEF_CORE_LAYOUT = toUldCoreLayout(DEF_FIELD_DEFS);
interface DefRow { upp_definition_id: number; internal_code: string; name: string; direction: 'EXPORT' | 'IMPORT'; entity_name: string | null; output_format: string; on_invalid: 'FLAG' | 'REJECT'; is_active: 'Y' | 'N' }
interface FieldRow { upp_field_id: number; source_key: string; target_key: string; sort_order: number; is_included: 'Y' | 'N'; transform: string; transform_config: unknown; validation: unknown }

const DEF_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code',      defaultWidth: 220 },
  { key: 'name',          header: 'Name',      defaultWidth: 240 },
  { key: 'direction',     header: 'Direction', defaultWidth: 110 },
  { key: 'output_format', header: 'Format',    defaultWidth: 100 },
  { key: 'on_invalid',    header: 'Invalid',   defaultWidth: 100 },
  { key: 'is_active',     header: 'Active',    defaultWidth: 80  },
];

export interface UppDefinitionManagerPageProps { session: AuthSession }

export function UppDefinitionManagerPage({ session }: UppDefinitionManagerPageProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <UppDefinitionManagerPageInner session={session} />
    </PersonalizationProvider>
  );
}

function UppDefinitionManagerPageInner({ session }: UppDefinitionManagerPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [rows, setRows] = useState<DefRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DefRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DefRow | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets (logical columns of the list).
  const [facets, setFacets] = useState<{ direction: string[]; format: string[]; on_invalid: string[]; active: string[] }>(
    { direction: [], format: [], on_invalid: [], active: ['Y'] },
  );

  const udcGrid = useUdcGrid('upp_definition', PAGE_CODE, { pkColumn: 'upp_definition_id', rows: rows as unknown as Record<string, unknown>[] });

  // §17.B5: distinct facet options derived from the loaded rows.
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort().map(v => ({ value: v, label: v }));
    return {
      direction: uniq(rows.map(r => r.direction)),
      format:    uniq(rows.map(r => r.output_format)),
      on_invalid: uniq(rows.map(r => r.on_invalid)),
      active: [
        { value: 'Y', label: t('common.boolean.active', 'Active') },
        { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
      ],
    };
  }, [rows, t]);

  const anyFilterActive = filter.trim().length > 0 ||
    facets.direction.length + facets.format.length + facets.on_invalid.length + facets.active.length > 0;
  const clearAll = () => { setFilter(''); setFacets({ direction: [], format: [], on_invalid: [], active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // A row passes a facet when no values are selected, or it matches one (OR within
    // a facet, AND across facets).
    const base = rows.filter(r => {
      const matchText = !q ||
        r.internal_code.toLowerCase().includes(q) ||
        (r.name ?? '').toLowerCase().includes(q) ||
        (r.entity_name ?? '').toLowerCase().includes(q);
      const matchDir    = facets.direction.length === 0  || facets.direction.includes(r.direction);
      const matchFormat = facets.format.length === 0     || facets.format.includes(r.output_format);
      const matchInvalid = facets.on_invalid.length === 0 || facets.on_invalid.includes(r.on_invalid);
      const matchActive = facets.active.length === 0     || facets.active.includes(r.is_active);
      return matchText && matchDir && matchFormat && matchInvalid && matchActive;
    });
    return base.filter(r => udcGrid.matchRow(r as unknown as Record<string, unknown>));
  }, [rows, filter, facets, udcGrid]);

  // §17.B13: expand/contract control. The list loads only definitions (field mappings
  // are fetched lazily inside the edit modal), so there are no related child collections
  // to surface here — the control renders inert.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [/* TODO: wire relations */];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('upp_definition')
        .select('upp_definition_id, internal_code, name, direction, entity_name, output_format, on_invalid, is_active').order('internal_code');
      if (e) throw e;
      setRows((data ?? []) as DefRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const handleDelete = async (row: DefRow) => {
    const { error: e } = await routedDelete(session, 'upp_definition', 'upp_definition_id', row.upp_definition_id);
    if (e) { setError(e.message); return; }
    setDeleteTarget(null);
    void reload();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('upp.title', 'Export / Import Definitions')}
          description={t('upp.list.description', 'Export and import profiles and the field mappings that drive the processing engine.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('upp.refresh', 'Refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('upp.new_definition', 'New Definition')}
              </button>)}
            </>
          }
        />

        <StatStrip
          stats={[
            { value: rows.length, label: t('upp.stat.total', 'Total definitions') },
            { value: rows.filter(r => r.is_active === 'Y').length, label: t('upp.stat.active', 'Active') },
          ]}
        />

        {error && (
          <ErrorBanner
            message={t('upp.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('upp.filter_definitions', 'Filter definitions…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('upp.col.direction', 'Direction')} options={facetOptions.direction} selected={facets.direction} onChange={v => setFacets(f => ({ ...f, direction: v }))} />
            <FacetFilter label={t('upp.col.output_format', 'Format')} options={facetOptions.format} selected={facets.format} onChange={v => setFacets(f => ({ ...f, format: v }))} />
            <FacetFilter label={t('upp.col.on_invalid', 'Invalid')} options={facetOptions.on_invalid} selected={facets.on_invalid} onChange={v => setFacets(f => ({ ...f, on_invalid: v }))} />
            <FacetFilter label={t('upp.col.is_active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('upp.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
        {busy && rows.length === 0 ? <SkeletonRows rows={8} /> : (
        <PersonalizedGrid componentKey="grid" columns={[...DEF_GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1 text-left" title={t('upp.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('upp.col.' + col.key, col.header)}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('upp.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.upp_definition_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.upp_definition_id)} hasChildren={hasRelated(r.upp_definition_id)} onToggle={() => detail.toggle(r.upp_definition_id)} expandLabel={t('upp.related.expand', 'Show related records')} collapseLabel={t('upp.related.collapse', 'Hide related')} tooltip={t('upp.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{r.internal_code}</span>}
                        {col.key === 'name'          && <span className="text-csm-sm">{r.name}</span>}
                        {col.key === 'direction'     && <span className="text-csm-xs">{r.direction}</span>}
                        {col.key === 'output_format' && <span className="text-csm-xs">{r.output_format}</span>}
                        {col.key === 'on_invalid'    && <span className="text-csm-xs">{r.on_invalid}</span>}
                        {col.key === 'is_active'     && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active')} noLabel={t('common.boolean.inactive')} />}
                        {udcGrid.isUdcKey(col.key)   && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                      {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('upp.edit', 'Edit')}</button>)}
                      {canDelete && (<button onClick={e => { e.stopPropagation(); setDeleteTarget(r); }} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('upp.delete', 'Delete')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.upp_definition_id) && (
                    <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                      <RelatedChildrenPanel groups={buildRelatedGroups(r.upp_definition_id)} session={session} />
                    </td></tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('upp.empty_title', 'No definitions yet')} action={canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('upp.new_definition_plus', '+ New Definition')}</button> : undefined} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>
      {(editing || creating) && (
        <DefinitionModal session={session} row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}
      {deleteTarget && (
        <ConfirmModal
          title={t('upp.delete_confirm_title', 'Delete Definition?')}
          message={t('upp.delete_confirm_message', 'Delete "{{name}}"? This cannot be undone.', { name: deleteTarget.name })}
          onConfirm={() => void handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function DefinitionModal({ session, row, onClose, onSaved }: { session: AuthSession; row: DefRow | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const id = row?.upp_definition_id;

  const initialValues = {
    code: row?.internal_code ?? '',
    name: row?.name ?? '',
    direction: (row?.direction ?? 'EXPORT') as 'EXPORT' | 'IMPORT',
    entity: row?.entity_name ?? '',
    format: row?.output_format ?? 'CSV',
    onInvalid: (row?.on_invalid ?? 'FLAG') as 'FLAG' | 'REJECT',
    active: row ? row.is_active === 'Y' : false,
  };

  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  // Custom fields placed on this entity (host = the list page) become first-class
  // FieldRenderer items: they interleave with native fields and inherit the same
  // per-tier personalization (hide / read-only / required / reorder).
  const udcForm = useUdcFormFields('upp_definition', PAGE_CODE, { pkColumn: 'upp_definition_id', pk: row?.upp_definition_id });
  const defCoreLayout = useMemo(() => [...DEF_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const defFieldDefs = useMemo(() => [...DEF_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const { layout } = useUldLayout('UPP.DEFINITION.MODAL', defCoreLayout);
  const [fields, setFields] = useState<FieldRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);

  const loadFields = async () => {
    if (!id) return;
    const { data } = await apiDb.from('upp_field').select('upp_field_id, source_key, target_key, sort_order, is_included, transform, transform_config, validation').eq('upp_definition_id', id).order('sort_order');
    setFields((data ?? []) as FieldRow[]);
  };

  useEffect(() => {
    reset(initialValues);
    setInternalCodeError(null);
    void loadFields();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const validateCode = (val: string) => {
    if (!val) { setInternalCodeError(null); return; }
    if (!/^[A-Z][A-Z0-9_.]*$/.test(val)) {
      setInternalCodeError(t('upp.code_validation', 'Must start with a letter — uppercase letters, numbers, dots only.'));
    } else {
      setInternalCodeError(null);
    }
  };

  const saveDef = async () => {
    setBusy(true); setError(null);
    try {
      if (isCreate && !form.code.trim()) throw new Error(t('upp.code_required', 'Code is required.'));
      if (internalCodeError) throw new Error(internalCodeError);
      const payload = { name: form.name.trim(), direction: form.direction, entity_name: form.entity.trim() || null, output_format: form.format, on_invalid: form.onInvalid, is_active: form.active ? 'Y' : 'N' };
      if (isCreate) { const { error: e } = await routedInsert(session, 'upp_definition', { internal_code: form.code.trim().toUpperCase(), ...payload }); if (e) throw e; }
      else { const { error: e } = await routedUpdate(session, 'upp_definition', 'upp_definition_id', id, payload); if (e) throw e; }
      reset(form);
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addField = async () => { if (!id) return; await routedInsert(session, 'upp_field', { upp_definition_id: id, source_key: '', target_key: '', sort_order: fields.length + 1, transform: 'NONE' }); await loadFields(); };
  const saveField = async (f: FieldRow, cfgText: string, valText: string) => {
    let cfg: unknown; let val: unknown;
    try { cfg = JSON.parse(cfgText || '{}'); val = JSON.parse(valText || '{}'); } catch { setError(t('upp.invalid_json', 'Transform config / validation must be valid JSON.')); return; }
    await routedUpdate(session, 'upp_field', 'upp_field_id', f.upp_field_id, { source_key: f.source_key, target_key: f.target_key, sort_order: f.sort_order, is_included: f.is_included, transform: f.transform, transform_config: cfg, validation: val });
  };
  const delField = async (fid: number) => { await routedDelete(session, 'upp_field', 'upp_field_id', fid); setFields(fs => fs.filter(x => x.upp_field_id !== fid)); };

  return (
    <>
      <Modal
        title={isCreate ? t('upp.modal_create_title', 'Create Definition') : t('upp.modal_edit_title', 'Edit Definition — {{code}}', { code: row?.internal_code ?? '' })}
        onClose={handleClose}
        width="3xl"
        headerAction={session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) ? null : undefined}
        footer={
          <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('upp.close', 'Close')}</button>
        }
      >
        <div className="mb-3">
          <FieldRenderer
            fields={defFieldDefs}
            layout={layout}
            columns={3}
            pageCode="UPP.DEFINITION.MODAL"
            session={session}
            render={(f) => {
              if (f.key === 'code') return (
                <PersonalizedField componentKey="modal" fieldKey="code" label={t('upp.field_code', 'Code')}>
                <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('upp.field_code', 'Code')}</label>
                  <input type="text" value={form.code} disabled={!isCreate}
                         onChange={e => setForm(ff => ({ ...ff, code: e.target.value.toUpperCase() }))}
                         onBlur={e => validateCode(e.target.value)}
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm disabled:bg-csm-surface-alt disabled:text-csm-muted" />
                  {internalCodeError
                    ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                    : <p className="text-csm-xs text-csm-ink-soft mt-1">{t('upp.code_hint', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>}
                </div>
                </PersonalizedField>
              );
              if (f.key === 'name') return (
                <PersonalizedField componentKey="modal" fieldKey="name" label={t('upp.field_name', 'Name')}>
                <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('upp.field_name', 'Name')}</label>
                  <input type="text" value={form.name} onChange={e => setForm(ff => ({ ...ff, name: e.target.value }))} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" /></div>
                </PersonalizedField>
              );
              if (f.key === 'direction') return (
                <PersonalizedField componentKey="modal" fieldKey="direction" label={t('upp.field_direction', 'Direction')}>
                <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('upp.field_direction', 'Direction')}</label>
                  <select value={form.direction} onChange={e => setForm(ff => ({ ...ff, direction: e.target.value as 'EXPORT' | 'IMPORT' }))} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm"><option value="EXPORT">EXPORT</option><option value="IMPORT">IMPORT</option></select></div>
                </PersonalizedField>
              );
              if (f.key === 'format') return (
                <PersonalizedField componentKey="modal" fieldKey="format" label={t('upp.field_format', 'Format')}>
                <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('upp.field_format', 'Format')}</label>
                  <select value={form.format} onChange={e => setForm(ff => ({ ...ff, format: e.target.value }))} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm">{['CSV', 'XLSX', 'PDF', 'JSON'].map(x => <option key={x} value={x}>{x}</option>)}</select></div>
                </PersonalizedField>
              );
              if (f.key === 'on_invalid') return (
                <PersonalizedField componentKey="modal" fieldKey="on_invalid" label={t('upp.field_on_invalid', 'On Invalid')}>
                <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('upp.field_on_invalid', 'On Invalid')}</label>
                  <select value={form.onInvalid} onChange={e => setForm(ff => ({ ...ff, onInvalid: e.target.value as 'FLAG' | 'REJECT' }))} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm"><option value="FLAG">FLAG</option><option value="REJECT">REJECT</option></select></div>
                </PersonalizedField>
              );
              if (f.key === 'entity') return (
                <PersonalizedField componentKey="modal" fieldKey="entity" label={t('upp.field_entity', 'Entity (optional)')}>
                <div><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('upp.field_entity', 'Entity (optional)')}</label>
                  <input type="text" value={form.entity} onChange={e => setForm(ff => ({ ...ff, entity: e.target.value }))} placeholder="csm_user_master" className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" /></div>
                </PersonalizedField>
              );
              if (udcForm.isUdcKey(f.key)) return (
                <PersonalizedField componentKey="modal" fieldKey={f.key} label={f.label}
                  readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
                  {udcForm.renderControl(f.key)}
                </PersonalizedField>
              );
              return null;
            }}
          />
        </div>
        <div className="mb-3 flex items-center gap-3">
          <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft"><input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="h-4 w-4 accent-gray-700" /> {t('upp.active', 'Active')}</label>
          <button onClick={() => void saveDef()} disabled={busy} className="ml-auto h-8 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('upp.saving', 'Saving…') : t('upp.save_definition', 'Save Definition')}</button>
        </div>

        {!isCreate && (
          <div className="border-t border-csm-border pt-2">
            <div className="mb-1 flex items-center gap-2">
              <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('upp.field_mappings', 'Field Mappings')}</span>
              <button onClick={() => void addField()} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('upp.add_field', '+ Field')}</button>
            </div>
            {fields.map((f, i) => (
              <div key={f.upp_field_id} className="mb-1 flex flex-wrap items-center gap-1">
                <input value={f.source_key} onChange={e => setFields(fs => fs.map((x, j) => j === i ? { ...x, source_key: e.target.value } : x))} onBlur={() => void saveField(fields[i], JSON.stringify(fields[i].transform_config ?? {}), JSON.stringify(fields[i].validation ?? {}))} placeholder={t('upp.source', 'source')} className="h-7 w-28 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                <span className="text-csm-2xs text-csm-watermark">→</span>
                <input value={f.target_key} onChange={e => setFields(fs => fs.map((x, j) => j === i ? { ...x, target_key: e.target.value } : x))} onBlur={() => void saveField(fields[i], JSON.stringify(fields[i].transform_config ?? {}), JSON.stringify(fields[i].validation ?? {}))} placeholder={t('upp.target', 'target')} className="h-7 w-28 rounded border border-csm-border px-1 font-mono text-csm-xs" />
                <input type="number" value={f.sort_order} onChange={e => setFields(fs => fs.map((x, j) => j === i ? { ...x, sort_order: Number(e.target.value) } : x))} onBlur={() => void saveField(fields[i], JSON.stringify(fields[i].transform_config ?? {}), JSON.stringify(fields[i].validation ?? {}))} className="h-7 w-12 rounded border border-csm-border px-1 text-csm-xs" title={t('upp.order', 'order')} />
                <select value={f.transform} onChange={e => setFields(fs => fs.map((x, j) => j === i ? { ...x, transform: e.target.value } : x))} onBlur={() => void saveField(fields[i], JSON.stringify(fields[i].transform_config ?? {}), JSON.stringify(fields[i].validation ?? {}))} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{TRANSFORMS.map(tr => <option key={tr} value={tr}>{tr}</option>)}</select>
                <input defaultValue={JSON.stringify(f.transform_config ?? {})} onBlur={e => void saveField(fields[i], e.target.value, JSON.stringify(fields[i].validation ?? {}))} placeholder="cfg {}" className="h-7 w-28 rounded border border-csm-border px-1 font-mono text-csm-2xs" title={t('upp.transform_config_json', 'transform config JSON')} />
                <input defaultValue={JSON.stringify(f.validation ?? {})} onBlur={e => void saveField(fields[i], JSON.stringify(fields[i].transform_config ?? {}), e.target.value)} placeholder='val {"required":true}' className="h-7 w-36 rounded border border-csm-border px-1 font-mono text-csm-2xs" title={t('upp.validation_json', 'validation JSON')} />
                <label className="flex items-center gap-1 text-csm-2xs"><input type="checkbox" checked={f.is_included === 'Y'} onChange={e => { setFields(fs => fs.map((x, j) => j === i ? { ...x, is_included: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveField(fields[i], JSON.stringify(fields[i].transform_config ?? {}), JSON.stringify(fields[i].validation ?? {}))} /> {t('upp.inc', 'inc')}</label>
                <button onClick={() => void delField(f.upp_field_id)} className="text-csm-danger text-csm-2xs">{t('upp.del', 'del')}</button>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="mt-3">
            <ErrorBanner
              message={t('upp.save_failed', "Couldn't save the definition.")}
              detail={error}
              details_label={t('common.details', 'Details')}
            />
          </div>
        )}
        {isCreate && <div className="mt-2 text-csm-2xs text-csm-watermark">{t('upp.save_first_note', 'Save the definition first to add field mappings.')}</div>}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('upp.discard_title', 'Discard changes?')}
          message={t('upp.discard_message', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('upp.discard_confirm', 'Discard')}
          cancelLabel={t('upp.discard_cancel', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(initialValues); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}
